import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import PaymentPlan from '../componets/PaymentPlan'
import { getDashboardData } from '../services/dashboard_service'
import './Dashboard.css'

const currencyLabels = [
  'balance',
  'sales',
  'revenue',
  'received',
  'collected',
  'value',
  'outstanding',
]

function formatMetricValue(metric) {
  const isCurrency = currencyLabels.some((label) =>
    metric.label.toLowerCase().includes(label),
  )
  if (isCurrency) {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      maximumFractionDigits: 0,
    }).format(Number(metric.value) || 0)
  }
  return Number(metric.value || 0).toLocaleString()
}

const firstDefined = (item, keys) => {
  const key = keys.find(
    (candidate) => item[candidate] !== undefined && item[candidate] !== null,
  )
  return key ? item[key] : undefined
}

function normalizeTrendItem(item, index) {
  const normalized = {
    ...item,
    month:
      firstDefined(item, ['month', 'label', 'period']) || String(index + 1),
  }
  const stockIn = firstDefined(item, [
    'in',
    'stock_in',
    'stockIn',
    'stockin',
    'total_in',
  ])
  const stockOut = firstDefined(item, [
    'out',
    'stock_out',
    'stockOut',
    'stockout',
    'total_out',
  ])
  const value = firstDefined(item, ['value', 'total', 'quantity', 'count'])

  if (stockIn !== undefined) normalized.in = stockIn
  if (stockOut !== undefined) normalized.out = stockOut
  if (value !== undefined) normalized.value = value
  return normalized
}

function MetricIcon({ tone }) {
  const paths = {
    orange: 'M5 4h14v16H5zM8 9h8M8 13h5',
    blue: 'M4 6h16v13H4zM7 3v3M17 3v3M4 10h16',
    purple:
      'M6 9V4h12v5M6 17H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2M6 14h12v7H6z',
    teal: 'M4 18V8m6 10V4m6 14v-7m4 7H2',
    green: 'm5 12 4 4L19 6',
    red: 'M12 8v5m0 4h.01M10.3 3.8 2.1 18a2 2 0 0 0 1.7 3h16.4a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0z',
    neutral: 'M4 7h16v13H4zM7 4h10v3M8 11h8M8 15h5',
  }
  return (
    <svg viewBox='0 0 24 24' aria-hidden='true'>
      <path d={paths[tone] || paths.neutral} />
    </svg>
  )
}

function Dashboard() {
  const { user, hasAnyPermission } = useAuth()
  const [dashboardData, setDashboardData] = useState(null)
  const [error, setError] = useState('')

  const userRole =
    user?.user_role ||
    user?.role_name ||
    user?.roleName ||
    user?.role ||
    'admin'
  const userId = user?.userid || user?.user_id || user?.id || ''

  useEffect(() => {
    let isMounted = true

    getDashboardData({ role: userRole, userId })
      .then((data) => {
        if (isMounted) setDashboardData(data)
      })
      .catch((requestError) => {
        if (isMounted) setError(requestError.message)
      })

    return () => {
      isMounted = false
    }
  }, [userId, userRole])

  if (error) {
    return (
      <div className='dashboard-page__error' role='alert'>
        {error}
      </div>
    )
  }

  if (!dashboardData) {
    return <div className='dashboard-page__loading'>Loading dashboard...</div>
  }

  const permissionByPath = {
    '/customers': ['viewCustomer'],
    '/samples': ['viewSamples'],
    '/sales/orders': ['viewOrders'],
    '/sales/orders/new': ['addOrder'],
    '/sales/payments': ['viewPayments'],
    '/sales/debtors': ['viewPayments'],
    '/sales/old-payments': ['viewOldPayment'],
    '/admin/stores': [
      'releaseOrder',
      'returnOrder',
      'releaseSample',
      'returnSample',
    ],
    '/admin/stock': ['addStock'],
    '/admin/print-request': ['viewPrints'],
  }
  const canOpenPath = (path) =>
    !path || hasAnyPermission(permissionByPath[path] || [])
  const metrics = (dashboardData.metrics || []).filter((item) =>
    canOpenPath(item.path),
  )
  const trend = (dashboardData.trend || []).map(normalizeTrendItem)
  const hasSplitTrend = trend.some(
    (item) => item.in !== undefined || item.out !== undefined,
  )
  const maxTrend = Math.max(
    ...trend.flatMap((item) =>
      hasSplitTrend
        ? [Number(item.in) || 0, Number(item.out) || 0]
        : [Number(item.value) || 0],
    ),
    1,
  )
  const recent = dashboardData.recent || []
  const queues = (dashboardData.queues || []).filter((item) =>
    canOpenPath(item.path),
  )

  return (
    <div className='dashboard-page'>
      <header className='dashboard-page__header'>
        <div>
          <span className='app-content__eyebrow'>Role workspace</span>
          <h1>{dashboardData.title}</h1>
          <p>{dashboardData.subtitle}</p>
        </div>
        <span className='dashboard-page__scope'>{dashboardData.scope}</span>
      </header>

      <section className='role-metrics' aria-label='Role performance summary'>
        {metrics.map((item) => {
          const content = (
            <>
              <span
                className={`role-metric__icon role-metric__icon--${item.tone}`}
              >
                <MetricIcon tone={item.tone} />
              </span>
              <span className='role-metric__label'>{item.label}</span>
              <strong>{formatMetricValue(item)}</strong>
              <small>{item.detail}</small>
            </>
          )
          return item.path ? (
            <Link className='role-metric' to={item.path} key={item.label}>
              {content}
            </Link>
          ) : (
            <article className='role-metric' key={item.label}>
              {content}
            </article>
          )
        })}
      </section>

      <section className='dashboard-workspace'>
        <article className='dashboard-panel dashboard-panel--trend'>
          <div className='dashboard-panel__header'>
            <div>
              <span className='app-content__eyebrow'>Performance</span>
              <h2>{dashboardData.trendLabel}</h2>
            </div>
            <span>{new Date().getFullYear()}</span>
          </div>
          {hasSplitTrend && (
            <div className='role-trend__legend' aria-label='Chart legend'>
              <span>
                <i className='role-trend__key role-trend__key--in' /> Stock in
              </span>
              <span>
                <i className='role-trend__key role-trend__key--out' /> Stock out
              </span>
            </div>
          )}
          {trend.length ? (
            <div className='role-trend' aria-label={dashboardData.trendLabel}>
              {trend.map((item) => {
                const stockIn = Number(item.in) || 0
                const stockOut = Number(item.out) || 0
                return (
                  <div className='role-trend__item' key={item.month}>
                    {hasSplitTrend ? (
                      <div className='role-trend__bars'>
                        <span
                          className='role-trend__track role-trend__track--in'
                          tabIndex='0'
                          aria-label={`${item.month} stock in: ${stockIn.toLocaleString()}`}
                        >
                          <i
                            style={{
                              height: `${Math.max((stockIn / maxTrend) * 100, 2)}%`,
                            }}
                          />
                          <span className='role-trend__tooltip'>
                            In: {stockIn.toLocaleString()}
                          </span>
                        </span>
                        <span
                          className='role-trend__track role-trend__track--out'
                          tabIndex='0'
                          aria-label={`${item.month} stock out: ${stockOut.toLocaleString()}`}
                        >
                          <i
                            style={{
                              height: `${Math.max((stockOut / maxTrend) * 100, 2)}%`,
                            }}
                          />
                          <span className='role-trend__tooltip'>
                            Out: {stockOut.toLocaleString()}
                          </span>
                        </span>
                      </div>
                    ) : (
                      <span
                        className='role-trend__track'
                        tabIndex='0'
                        aria-label={`${item.month}: ${Number(item.value || 0).toLocaleString()}`}
                      >
                        <i
                          style={{
                            height: `${Math.max((Number(item.value) / maxTrend) * 100, 2)}%`,
                          }}
                        />
                        <span className='role-trend__tooltip'>
                          {Number(item.value || 0).toLocaleString()}
                        </span>
                      </span>
                    )}
                    <small>{item.month}</small>
                    <strong>
                      {hasSplitTrend
                        ? (stockIn + stockOut).toLocaleString()
                        : Number(item.value || 0).toLocaleString()}
                    </strong>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className='dashboard-panel__empty'>
              Operational queues are shown alongside current workload.
            </div>
          )}
        </article>

        <article className='dashboard-panel dashboard-panel--queues'>
          <div className='dashboard-panel__header'>
            <div>
              <span className='app-content__eyebrow'>Next actions</span>
              <h2>Work queues</h2>
            </div>
          </div>
          <div className='role-queues'>
            {queues.map((queue) => (
              <Link to={queue.path} key={queue.label}>
                <span>{queue.label}</span>
                <strong>{queue.count ?? 'Open'}</strong>
                <span aria-hidden='true'>→</span>
              </Link>
            ))}
          </div>
        </article>
      </section>

      <section className='dashboard-panel dashboard-panel--recent'>
        <div className='dashboard-panel__header'>
          <div>
            <span className='app-content__eyebrow'>Latest activity</span>
            <h2>Recent work</h2>
          </div>
        </div>
        {recent.length ? (
          <div className='role-recent'>
            {recent.map((item, index) => (
              <div className='role-recent__row' key={`${item.id}-${index}`}>
                <strong>{item.id}</strong>
                <span>{item.title || 'Unassigned'}</span>
                <small>{item.meta}</small>
                <em>{item.status}</em>
              </div>
            ))}
          </div>
        ) : (
          <div className='dashboard-panel__empty'>
            No recent work in this scope.
          </div>
        )}
      </section>
      {dashboardData.role === 'sales' && (
        <PaymentPlan overduePayments={dashboardData.overduePaymentPlans} />
      )}
    </div>
  )
}
export default Dashboard
